import { useCallback, useMemo } from 'react'

import {
  type F0FormSubmitResult,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import { useCreateTagAction } from 'modules/ats/lib/utils/useCreateTagAction'

const buildSchema = () =>
  z.object({
    name: f0FormField(z.string().min(1, i18n.t('common.validations.empty')), {
      label: i18n.t('ats.tags.create_form.name_label'),
      fieldType: 'text',
    }),
  })

type NewTagFormSchema = ReturnType<typeof buildSchema>
type NewTagFormValues = z.infer<NewTagFormSchema>

interface Props {
  companyId: string
}

export const useForm = ({ companyId }: Props) => {
  const { formRef, reset } = useF0Form()
  const { createTag } = useCreateTagAction({ companyId })

  const schema = useMemo(() => buildSchema(), [])

  const handleSubmit = useCallback(
    async ({ data }: { data: NewTagFormValues }): Promise<F0FormSubmitResult> => {
      const result = await createTag(data.name)

      if (result.success) {
        reset()
      }

      return result
    },
    [createTag, reset]
  )

  const formDefinition = useF0FormDefinition({
    name: 'ats-new-tag',
    schema,
    defaultValues: {
      name: '',
    },
    submitConfig: {
      type: 'default',
      label: i18n.t('common.save'),
    },
    errorTriggerMode: 'on-submit',
    onSubmit: handleSubmit,
  })

  return {
    formDefinition,
    formRef,
  }
}
